/* eslint-disable no-param-reassign */
import React from 'react';
import type { TreeItem } from '@ditioas/web-ui';
import {
  differenceInDays,
  eachDayOfInterval,
  format,
  parseISO,
} from 'date-fns';
import type { PlansContextParams } from 'containers/ResourcePlanning/Scheduler/contexts/PlansContext/PlansContext';
import { useGetPlanningSettings } from 'data/resourcePlanning';
import { localeCompare } from 'libs/sortFunctions';
import type { ProjectTask } from 'model/Projects/types';

import { formatISOWithoutTime, localeFormat } from 'libs/dateFunctions';
import type {
  CertificateOrTagOption,
  DayPlan,
  MachineDayPlan,
  MachineForPlanning,
  MachinePlan,
  MachinePlanningTask,
  PlanningShift,
  SpanningShifts,
  UserForPlanning,
  UserPlan,
} from 'model/ResourcePlanning/types';
import {
  isUserForPlanningArray,
  isUserPlanGuard,
} from 'model/ResourcePlanning/types';
import type {
  Certificate,
  EmployeeAndSubcontractorUser,
} from 'model/User/types';

const today = new Date();
const lastAvailableYear =
  today.getMonth() < 9 ? today.getFullYear() : today.getFullYear() + 1;
export const availableYears = [2021];
for (let y = availableYears[0]; y < lastAvailableYear; y += 1) {
  availableYears.push(y + 1);
}

export const plannerDateFormat = 'yyyy-MM-dd';
const timeSuffix = 'T00:00:00';

export const getPlannerDateString = (date: Date | string) => {
  if (typeof date === 'string') {
    return `${formatISOWithoutTime(parseISO(date))}${timeSuffix}`;
  }

  return `${formatISOWithoutTime(date)}${timeSuffix}`;
};

const getFirstMonday = (year: number) => {
  // Get the first day of the year
  const firstDay = new Date(year, 0, 1); // January 1st
  // Get the first Monday which could be a day before January 1st
  const firstMonday = new Date(year, 0, 2 - firstDay.getDay());

  return firstMonday;
};

export const getWeekForPlanning = (date: Date) => {
  const year = date.getFullYear();
  const firstMonday = getFirstMonday(year);

  // If the date is before the first Monday, consider it as week 0
  if (date < firstMonday) {
    return 0;
  }

  // Calculate the difference in days
  const daysDifference = differenceInDays(date, firstMonday);
  // Determine the week number
  const weekNumber = Math.floor(daysDifference / 7) + 1;
  return weekNumber;
};

// Ended up not being used might was left here in case it might be needed later
const getFirstAndLastDayOfWeekByDate = (date: Date) => {
  const year = date.getFullYear();
  const weekNumber = getWeekForPlanning(date);
  const firstMonday = getFirstMonday(year);

  // Calculate the first day of the week
  const firstDayOfWeek = new Date(firstMonday.getTime());
  firstDayOfWeek.setDate(firstMonday.getDate() + (weekNumber - 1) * 7);

  // Calculate the last day of the week
  const lastDayOfWeek = new Date(firstDayOfWeek.getTime());
  lastDayOfWeek.setDate(lastDayOfWeek.getDate() + 6);

  return {
    firstDayOfWeek,
    lastDayOfWeek,
  };
};

export const getFirstAndLastDayOfWeek = (week: number, year: number) => {
  const firstMonday = getFirstMonday(year);

  // Calculate the first day of the given week
  const firstDayOfWeek = new Date(firstMonday.getTime());
  firstDayOfWeek.setDate(firstMonday.getDate() + (week - 1) * 7);

  // Calculate the last day of the given week
  const lastDayOfWeek = new Date(firstDayOfWeek.getTime());
  lastDayOfWeek.setDate(lastDayOfWeek.getDate() + 6);

  return {
    firstDayOfWeek,
    lastDayOfWeek,
  };
};

// useDrag/useDrop do not accept number as a type that's why this is not an enum
export const CardDragType = {
  DragToMove: 'DRAG_TO_MOVE',
  DragToCopy: 'DRAG_TO_COPY',
  DragToCreate: 'DRAG_TO_CREATE',
};

export const getQueryString = (week: number, year: number) => {
  const { firstDayOfWeek, lastDayOfWeek } = getFirstAndLastDayOfWeek(
    week,
    year
  );

  return `fromDate=${format(firstDayOfWeek, plannerDateFormat)}&toDate=${format(
    lastDayOfWeek,
    plannerDateFormat
  )}`;
};

export const getDatesWithinWeek = (week: number, year: number) => {
  const { firstDayOfWeek, lastDayOfWeek } = getFirstAndLastDayOfWeek(
    week,
    year
  );

  const datesWithinWeek = eachDayOfInterval({
    start: firstDayOfWeek,
    end: lastDayOfWeek,
  }).map((date) => ({
    date,
    monthName: localeFormat(date, 'MMMM'),
    dayNameAndNumber: localeFormat(date, 'eeeeee d'),
    isToday:
      getWeekForPlanning(new Date()) === week &&
      new Date().getDate() === date.getDate(),
  }));

  return datesWithinWeek;
};

export const filterUsers = ({
  userIdsForProject,
  soonToBeOrActiveUsersById,
  certificates,
  userSearch,
  selectedFilterOptions,
  lastDayOfWeek,
  newlyAddedUsers,
  isSearchingUnassigned = false,
}: {
  userIdsForProject: string[];
  soonToBeOrActiveUsersById: {
    [key: string]: EmployeeAndSubcontractorUser;
  };
  certificates: Certificate[];
  userSearch: string;
  selectedFilterOptions: CertificateOrTagOption[];
  lastDayOfWeek: Date;
  newlyAddedUsers: string[];
  isSearchingUnassigned?: boolean;
}) => {
  const relevantUserIds = isSearchingUnassigned
    ? Object.keys(soonToBeOrActiveUsersById).filter(
        (userId) =>
          !userIdsForProject.includes(userId) &&
          !newlyAddedUsers.includes(userId)
      )
    : userIdsForProject;

  if (userSearch?.length > 0 && selectedFilterOptions?.length > 0) {
    const selectedTagIds: string[] = [];
    const selectedCertificateTypeIds: string[] = [];
    selectedFilterOptions.forEach(({ id, isTag }) => {
      if (isTag) {
        selectedTagIds.push(id);
      } else {
        selectedCertificateTypeIds.push(id);
      }
    });
    const filteredUserIds = relevantUserIds.filter((userId) => {
      const user = soonToBeOrActiveUsersById[userId];
      // a hack cuz backend returns inactive users
      if (!user) {
        console.warn('Tried to search for a user that is not active:', userId);
        return false;
      }
      // The first check makes sure that user's name includes searched string
      if (!user.fullName.toLowerCase().includes(userSearch.toLowerCase())) {
        // Next check makes sure that user's employee number includes searched string
        if (
          user.employment?.employeeNumber &&
          !user.employment.employeeNumber
            .toLowerCase()
            .includes(userSearch.toLowerCase())
        ) {
          return false;
        }
      }

      const userTagIds = user.userTags.map((tag) => tag.tagId) || [];

      if (selectedTagIds.length) {
        // If tags were selected
        if (selectedCertificateTypeIds.length) {
          // If certificate types were selected as well
          if (
            selectedTagIds.find((tagId) => userTagIds.includes(tagId)) &&
            certificates.find((certificate) => {
              let validTo: Date | null = null;
              if (certificate.validTo) {
                validTo = new Date(certificate.validTo);
                validTo.setHours(0, 0, 0, 0);
              }
              return (
                certificate.userId === userId &&
                selectedCertificateTypeIds.includes(certificate.typeId) &&
                validTo &&
                (!certificate.validTo || validTo >= lastDayOfWeek)
              );
            })
          ) {
            // If user has one of the tags and one of the certificate types
            return true;
          }

          return false;
        }
        if (!selectedTagIds.find((tagId) => userTagIds.includes(tagId))) {
          // If user doesn't have any of selected tags
          return false;
        }
      } else if (selectedCertificateTypeIds.length) {
        // If only certificate types were selected
        if (
          !certificates.find((certificate) => {
            let validTo: Date | null = null;
            if (certificate.validTo) {
              validTo = new Date(certificate.validTo);
              validTo.setHours(0, 0, 0, 0);
            }
            return (
              certificate.userId === userId &&
              selectedCertificateTypeIds.includes(certificate.typeId) &&
              validTo &&
              (!certificate.validTo || validTo >= lastDayOfWeek)
            );
          })
        ) {
          // If user doesn't have any certificate or doesn't have a certificated of a selected type
          return false;
        }
      }

      return true;
    });

    return isSearchingUnassigned
      ? filteredUserIds.map((userId) => soonToBeOrActiveUsersById[userId])
      : filteredUserIds;
  }

  if (userSearch?.length > 0) {
    const filteredUserIds = relevantUserIds.filter((userId) => {
      const user = soonToBeOrActiveUsersById[userId];
      // a hack cuz backend returns inactive users
      if (!user) {
        console.warn('Tried to search for a user that is not active:', userId);
        return false;
      }

      return (
        user.fullName.toLowerCase().includes(userSearch.toLowerCase()) ||
        user.employment?.employeeNumber
          ?.toLowerCase()
          .includes(userSearch.toLowerCase())
      );
    });

    return isSearchingUnassigned
      ? filteredUserIds.map((userId) => soonToBeOrActiveUsersById[userId])
      : filteredUserIds;
  }

  if (selectedFilterOptions?.length > 0) {
    const selectedTagIds: string[] = [];
    const selectedCertificateTypeIds: string[] = [];
    selectedFilterOptions.forEach(({ id, isTag }) => {
      if (isTag) {
        selectedTagIds.push(id);
      } else {
        selectedCertificateTypeIds.push(id);
      }
    });
    const filteredUserIds = relevantUserIds.filter((userId) => {
      const user = soonToBeOrActiveUsersById[userId];

      if (!user) {
        console.warn('Tried to search for a user that is not active:', userId);
        return false;
      }

      const userTagIds = user.userTags.map((tag) => tag.tagId) || [];

      if (selectedTagIds.length) {
        // If tags were selected
        if (selectedCertificateTypeIds.length) {
          // If certificate types were selected as well
          if (
            selectedTagIds.find((tagId) => userTagIds.includes(tagId)) &&
            certificates.find((certificate) => {
              let validTo: Date | null = null;
              if (certificate.validTo) {
                validTo = new Date(certificate.validTo);
                validTo.setHours(0, 0, 0, 0);
              }
              return (
                certificate.userId === userId &&
                selectedCertificateTypeIds.includes(certificate.typeId) &&
                validTo &&
                (!certificate.validTo || validTo >= lastDayOfWeek)
              );
            })
          ) {
            // If user has one of the tags and one of the certificate types
            return true;
          }

          return false;
        }
        if (!selectedTagIds.find((tagId) => userTagIds.includes(tagId))) {
          // If user doesn't have any of selected tags
          return false;
        }
      } else if (selectedCertificateTypeIds.length) {
        // If only certificate types were selected
        if (
          !certificates.find((certificate) => {
            let validTo: Date | null = null;
            if (certificate.validTo) {
              validTo = new Date(certificate.validTo);
              validTo.setHours(0, 0, 0, 0);
            }
            return (
              certificate.userId === userId &&
              selectedCertificateTypeIds.includes(certificate.typeId) &&
              validTo &&
              (!certificate.validTo || validTo >= lastDayOfWeek)
            );
          })
        ) {
          // If user doesn't have any certificate or doesn't have a certificated of a selected type
          return false;
        }
      }

      return true;
    });

    return isSearchingUnassigned
      ? filteredUserIds.map((userId) => soonToBeOrActiveUsersById[userId])
      : filteredUserIds;
  }

  return isSearchingUnassigned
    ? relevantUserIds.map((userId) => soonToBeOrActiveUsersById[userId])
    : userIdsForProject;
};

export const useGetFullTaskName = () => {
  const {
    data: {
      settings: { showTaskNumber },
    },
  } = useGetPlanningSettings();

  return (plan: UserPlan | MachinePlan) =>
    `${showTaskNumber && plan.taskNumber ? `${plan.taskNumber}: ` : ''}${
      plan.taskName
    }`;
};

export const filterConcurrentItems = (
  plans: UserForPlanning[],
  editedTask: UserPlan | MachinePlan
): UserForPlanning[] => {
  const replacedDate = editedTask.date.replace('Z', '');

  if (isUserForPlanningArray(plans) && isUserPlanGuard(editedTask)) {
    return plans.filter(
      (userPlan) =>
        userPlan.userId !== editedTask.userId &&
        userPlan.plans[replacedDate]?.projectPlans?.find(
          (plan) => plan.taskId === editedTask.taskId
        )
    );
  }

  return plans.filter((plan) =>
    plan.plans[replacedDate]?.projectPlans?.find(
      (anotherPlan) =>
        anotherPlan.taskId === editedTask.taskId &&
        anotherPlan.machineIds.includes(
          (editedTask as MachinePlanningTask).machineId
        )
    )
  );
};

export const getShiftKey = (shift: PlanningShift) => {
  if (!shift.id) {
    return '';
  }

  return shift.shiftCode && shift.colorCode
    ? `${shift.shiftCode}-${shift.colorCode}`
    : shift.id;
};

export const setSpanningShifts = (
  plans: DayPlan[],
  spanningShifts: SpanningShifts
) =>
  plans.forEach((dayPlan, dayIndex) =>
    dayPlan.shifts
      .filter((shift) => !shift.isAbsence && !shift.isContingencyShift)
      .forEach((shift) => {
        const key = getShiftKey(shift);
        const end = dayIndex + 2;
        if (key) {
          if (spanningShifts[key]) {
            if (end - spanningShifts[key].end === 1) {
              spanningShifts[key].end = end;
              spanningShifts[key].weight += 1;
            } else {
              const prevIndex = Object.entries(spanningShifts).findIndex(
                ([shiftKey, prevShift]) =>
                  shiftKey !== key &&
                  shiftKey.includes(key) &&
                  end - prevShift.end === 1
              );
              if (prevIndex === -1) {
                spanningShifts[`${key}-${end}`] = {
                  start: dayIndex + 1,
                  end,
                  weight: 1,
                };
              } else {
                const prevShiftKey = Object.keys(spanningShifts)[prevIndex];
                spanningShifts[prevShiftKey].end = end;
                spanningShifts[prevShiftKey].weight += 1;
              }
            }
          } else {
            spanningShifts[key] = {
              start: dayIndex + 1,
              end,
              weight: 1,
            };
          }
        }
      })
  );

export const planPlaceholderBase = {
  id: null,
  createdAt: new Date().toISOString(),
  defaultResourceId: '',
  departmentCode: '',
  isAutoPlanned: false,
  isChapter: false,
  modifiedAt: new Date().toISOString(),
  projectName: '',
  moveReason: null,
  moveCategory: null,
  moveCause: null,
  recVersion: 0,
  refId: 'placeholder',
  workDescription: null,
};

export const sortTree = (itemA: TreeItem, itemB: TreeItem) => {
  if (itemA.isFolder) {
    if (itemB.isFolder) {
      return localeCompare(itemA.name, itemB.name);
    }

    return -1;
  }
  if (itemB.isFolder) {
    return 1;
  }

  return localeCompare(itemA.name, itemB.name);
};

export const mapTasksForTreeView = (
  tasks: ProjectTask[],
  disabledIds: string[] = []
) =>
  tasks.reduce((pv, cv) => {
    if (!cv.isFolder || cv.childNodes.length) {
      pv.push({
        id: cv.taskId,
        name: cv.taskName,
        keywords: cv.taskNumber ? [cv.taskNumber] : undefined,
        children: mapTasksForTreeView(cv.childNodes, disabledIds).sort(
          sortTree
        ),
        isFolder: cv.isFolder,
        disabled: disabledIds.includes(cv.taskId),
        display: cv.childNodes.length ? undefined : (
          <>
            {cv.taskNumber && (
              <p className="text-sand-1100 body-xxs">{cv.taskNumber}</p>
            )}
            <p className="body-xs">{cv.taskName}</p>
          </>
        ),
        className: cv.childNodes.length
          ? undefined
          : 'flex-col items-start justify-center gap-0',
      });
    }

    return pv;
  }, [] as TreeItem[]);

type ParamsForUpdatingPlansContext = {
  plansContext: PlansContextParams;
} & (
  | {
      isUserPlan: true;
      entityForPlanning: UserForPlanning;
    }
  | {
      isUserPlan: false;
      entityForPlanning: MachineForPlanning;
    }
);

/**
 * Updates the plans context with the new plans for the given entity.
 * @param plansContext - The plans context to update.
 * @param isUserPlan - Whether the entity is a user or a machine.
 * @param entityForPlanning - UserForPlanning or MachineForPlanning.
 */
export const updatePlansContext = ({
  plansContext,
  isUserPlan,
  entityForPlanning,
}: ParamsForUpdatingPlansContext) => {
  const plansByYearsAndWeeks = Object.entries(entityForPlanning.plans).reduce(
    (pv, [dateString, dayPlan]) => {
      const date = parseISO(dateString);
      const week = getWeekForPlanning(date);
      const year = date.getFullYear();

      if (pv[year]) {
        if (pv[year][week]) {
          pv[year][week][dateString] = dayPlan;
        } else {
          pv[year][week] = { [dateString]: dayPlan };
        }
      } else {
        pv[year] = {
          [week]: { [dateString]: dayPlan },
        };
      }

      return pv;
    },
    {} as {
      [year: number]: {
        [week: number]: {
          [date: string]: DayPlan | MachineDayPlan;
        };
      };
    }
  );

  Object.entries(plansByYearsAndWeeks).forEach(([year, plansByWeeks]) => {
    Object.entries(plansByWeeks).forEach(([week, plans]) => {
      if (isUserPlan) {
        if (plansContext.plans[entityForPlanning.userId]) {
          if (plansContext.plans[entityForPlanning.userId][year]) {
            if (plansContext.plans[entityForPlanning.userId][year][week]) {
              plansContext.plans[entityForPlanning.userId][year][week] = {
                ...entityForPlanning,
                plans: plans as { [date: string]: DayPlan },
              };
            } else {
              plansContext.plans[entityForPlanning.userId][year][week] = {
                ...entityForPlanning,
                plans: plans as { [date: string]: DayPlan },
              };
            }
          } else {
            plansContext.plans[entityForPlanning.userId][year] = {
              [week]: {
                ...entityForPlanning,
                plans: plans as { [date: string]: DayPlan },
              },
            };
          }
        } else {
          plansContext.plans[entityForPlanning.userId] = {
            [year]: {
              [week]: {
                ...entityForPlanning,
                plans: plans as { [date: string]: DayPlan },
              },
            },
          };
        }
      } else if (plansContext.machinePlans[entityForPlanning.machineId]) {
        if (plansContext.machinePlans[entityForPlanning.machineId][year]) {
          if (
            plansContext.machinePlans[entityForPlanning.machineId][year][week]
          ) {
            plansContext.machinePlans[entityForPlanning.machineId][year][week] =
              {
                ...entityForPlanning,
                plans: plans as { [date: string]: MachineDayPlan },
              };
          } else {
            plansContext.machinePlans[entityForPlanning.machineId][year][week] =
              {
                ...entityForPlanning,
                plans: plans as { [date: string]: MachineDayPlan },
              };
          }
        } else {
          plansContext.machinePlans[entityForPlanning.machineId][year] = {
            [week]: {
              ...entityForPlanning,
              plans: plans as { [date: string]: MachineDayPlan },
            },
          };
        }
      } else {
        plansContext.machinePlans[entityForPlanning.machineId] = {
          [year]: {
            [week]: {
              ...entityForPlanning,
              plans: plans as { [date: string]: MachineDayPlan },
            },
          },
        };
      }
    });
  });

  return plansContext;
};
